import {
  CreateThirdParty,
  FilterArgs,
  InvitePortalUser,
  OwnerId,
  Paginated,
  PaginationArgs,
  PortalUserMin,
  ThirdParty,
  ThirdPartyId,
  ThirdPartyTagId,
  ThirdPartyTierId,
  UserId,
} from "platformed-client/types";
import {invalidateQueries, q} from "../../state";
import {platformedClient} from "..";

export async function list(query: PaginationArgs, body: FilterArgs): Promise<Paginated<ThirdParty>> {
  return await platformedClient.internalRequest("post", "/region/{region}/api/tprm/third_parties/search", {
    query,
    body,
  });
}

export async function get(thirdPartyId: ThirdPartyId): Promise<ThirdParty> {
  return await platformedClient.internalRequest("get", "/region/{region}/api/tprm/third_parties/{third_party_id}", {
    path: {third_party_id: thirdPartyId},
  });
}

export async function create(thirdParty: CreateThirdParty): Promise<ThirdParty> {
  const res = await platformedClient.internalRequest("post", "/region/{region}/api/tprm/third_parties", {
    body: thirdParty,
  });
  await invalidateQueries([q.tprm.thirdParties.filter()]);
  return res;
}

export async function delete_(thirdPartyId: ThirdPartyId): Promise<string> {
  const res = await platformedClient.internalRequest(
    "delete",
    "/region/{region}/api/tprm/third_parties/{third_party_id}",
    {path: {third_party_id: thirdPartyId}},
  );
  await invalidateQueries([q.tprm.thirdParties.filter()]);
  return res;
}

export async function updateName(thirdPartyId: ThirdPartyId, name: string): Promise<string> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/third_parties/{third_party_id}/name",
    {path: {third_party_id: thirdPartyId}, body: name},
  );
  await invalidateQueries([
    q.tprm.thirdParties.filter(),
    q.tprm.thirdParty.filter(thirdPartyId),
    // Renaming a third party also renames the global scope across all its data rooms. These queries
    // are a little coarse but do the job.
    q.tprm.engagementScopes.filter(),
    q.tprm.scope.filter(),
  ]);
  return res;
}

export async function updateUrl(thirdPartyId: ThirdPartyId, url: string): Promise<string> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/third_parties/{third_party_id}/url",
    {path: {third_party_id: thirdPartyId}, body: url},
  );
  await invalidateQueries([q.tprm.thirdParties.filter(), q.tprm.thirdParty.filter(thirdPartyId)]);
  return res;
}

export async function updateTier(thirdPartyId: ThirdPartyId, tierId: ThirdPartyTierId | null): Promise<string> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/third_parties/{third_party_id}/tier_id",
    {path: {third_party_id: thirdPartyId}, body: tierId},
  );
  await invalidateQueries([q.tprm.thirdParties.filter(), q.tprm.thirdParty.filter(thirdPartyId)]);
  return res;
}

export async function updateExternalId(thirdPartyId: ThirdPartyId, externalId: string): Promise<string> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/third_parties/{third_party_id}/external_id",
    {path: {third_party_id: thirdPartyId}, body: externalId},
  );
  await invalidateQueries([q.tprm.thirdParties.filter(), q.tprm.thirdParty.filter(thirdPartyId)]);
  return res;
}

export async function updateTags(thirdPartyId: ThirdPartyId, tagIds: ThirdPartyTagId[]): Promise<string> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/third_parties/{third_party_id}/tag_ids",
    {path: {third_party_id: thirdPartyId}, body: tagIds},
  );
  await invalidateQueries([q.tprm.thirdParties.filter(), q.tprm.thirdParty.filter(thirdPartyId)]);
  return res;
}

export async function updateOwner(thirdPartyId: ThirdPartyId, ownerId: OwnerId | null): Promise<string> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/third_parties/{third_party_id}/owner_id",
    {path: {third_party_id: thirdPartyId}, body: ownerId},
  );
  await invalidateQueries([q.tprm.thirdParties.filter(), q.tprm.thirdParty.filter(thirdPartyId)]);
  return res;
}

export async function listPortalUsers(thirdPartyId: ThirdPartyId): Promise<PortalUserMin[]> {
  return await platformedClient.internalRequest(
    "get",
    "/region/{region}/api/tprm/third_parties/{third_party_id}/portal/users",
    {path: {third_party_id: thirdPartyId}},
  );
}

export async function invitePortalUser(thirdPartyId: ThirdPartyId, payload: InvitePortalUser): Promise<string> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/third_parties/{third_party_id}/portal/invite_user",
    {path: {third_party_id: thirdPartyId}, body: payload},
  );
  await invalidateQueries([q.tprm.thirdPartyPortalUsers.filter(thirdPartyId)]);
  return res;
}

export async function deletePortalUser(thirdPartyId: ThirdPartyId, userId: UserId): Promise<string> {
  const res = await platformedClient.internalRequest(
    "delete",
    "/region/{region}/api/tprm/third_parties/{third_party_id}/portal/users/{user_id}",
    {path: {third_party_id: thirdPartyId, user_id: userId}},
  );
  await invalidateQueries([q.tprm.thirdPartyPortalUsers.filter(thirdPartyId)]);
  return res;
}

export async function makePrivate(thirdPartyId: ThirdPartyId): Promise<string> {
  const res = await platformedClient.internalRequest(
    "post",
    "/region/{region}/api/tprm/third_parties/{third_party_id}/private",
    {path: {third_party_id: thirdPartyId}},
  );
  await invalidateQueries([q.tprm.thirdParties.filter()]);
  await invalidateQueries([q.tprm.thirdParty.filter(thirdPartyId)]);
  return res;
}
